import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useOSPulse, eventLabel, timeAgo } from '../hooks/useOSPulse';
import { useT } from '../i18n';

/* OSTicker — slim live strip fed by real /api/pulse events.
   Rotates one message at a time; links to the article or /os. */

export default function OSTicker() {
  const pulse = useOSPulse();
  const { lang } = useT();
  const es = lang === 'es';
  const [idx, setIdx] = useState(0);

  const events = (pulse?.events || []).slice(0, 8);

  useEffect(() => {
    if (events.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % events.length), 5000);
    return () => clearInterval(t);
  }, [events.length]);

  if (!events.length) return null;
  const e = events[idx % events.length];

  return (
    <div className="border-b border-white/[0.06] mb-12 lg:mb-14 pb-6" data-testid="os-ticker">
      <div className="flex items-center gap-3 min-w-0">
        <span className="inline-flex items-center gap-1.5 shrink-0 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
          </span>
          {es ? 'OS en vivo' : 'OS Live'}
        </span>
        <div className="relative flex-1 min-w-0 h-5 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={idx}
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -14, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 text-[13px] text-[#B8BDC6] truncate"
            >
              {e.slug ? (
                <Link to={`/magazine/${e.slug}`} className="hover:text-[#FF7A00] transition-colors duration-200">
                  {eventLabel(e, es)}
                </Link>
              ) : (
                eventLabel(e, es)
              )}
              <span className="text-[#7D848E]"> · {timeAgo(e.created_at, es)}</span>
            </motion.p>
          </AnimatePresence>
        </div>
        <Link
          to="/os"
          className="hidden sm:inline-flex items-center gap-1 shrink-0 text-[11.5px] font-semibold text-[#7D848E] hover:text-[#FF7A00] transition-colors duration-200"
          data-testid="os-ticker-link"
        >
          {es ? 'Ver el OS trabajando' : 'Watch the OS work'} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
